import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { campaignService, contributionService } from '../../../services/crowdfunding';
import { formatCurrency } from '../../../utils/formatters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Download, TrendingUp, Users, Award, Heart, DollarSign, Calendar } from 'lucide-react';

export const CampaignStats: React.FC = () => {
  useCheckPermissions(['admin']);
  
  // Récupérer les statistiques globales des campagnes
  const tenantId = import.meta.env.VITE_TENANT_ID || '2';
  
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['campaign-stats', tenantId],
    queryFn: async () => await campaignService.getCampaignsStats(tenantId),
    refetchInterval: 60000 // Rafraîchir toutes les minutes
  });
  
  // Récupérer les contributions récentes
  const { data: recentContributions, isLoading: isLoadingContributions } = useQuery({
    queryKey: ['recent-contributions', tenantId],
    queryFn: async () => await contributionService.getRecentContributions(5, tenantId),
    refetchInterval: 30000 // Rafraîchir toutes les 30 secondes
  });
  
  // Données pour le graphique des campagnes par statut
  const campaignStatusData = stats ? [
    { name: 'Actives', value: stats.activeCampaigns, color: '#22c55e' },
    { name: 'Terminées', value: stats.completedCampaigns, color: '#3b82f6' },
    { name: 'Autres', value: stats.totalCampaigns - stats.activeCampaigns - stats.completedCampaigns, color: '#6b7280' }
  ] : [];
  
  // Données pour le graphique des montants collectés vs objectifs
  const amountData = stats ? [
    { name: 'Collecté', value: stats.totalCollectedAmount, color: '#eab308' },
    { name: 'Restant', value: Math.max(0, stats.totalGoalAmount - stats.totalCollectedAmount), color: '#6b7280' }
  ] : [];
  
  // Fonction pour exporter les statistiques en CSV
  const exportStatsToCSV = () => {
    if (!stats) return;
    
    const headers = ['Métrique', 'Valeur'];
    const rows = [
      ['Campagnes totales', stats.totalCampaigns],
      ['Campagnes actives', stats.activeCampaigns],
      ['Campagnes terminées', stats.completedCampaigns],
      ['Taux de réussite', `${stats.successRate.toFixed(2)}%`],
      ['Montant total objectif', stats.totalGoalAmount],
      ['Montant total collecté', stats.totalCollectedAmount],
      ['Pourcentage atteint', `${((stats.totalCollectedAmount / stats.totalGoalAmount) * 100).toFixed(2)}%`],
      ['Contributions totales', stats.totalContributions]
    ];
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `statistiques_crowdfunding_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Statistiques Crowdfunding</h1>
            <p className="text-gray-400">
              Tableau de bord des statistiques de financement participatif
            </p>
          </div>
          
          <Button onClick={exportStatsToCSV} disabled={isLoadingStats}>
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </Button>
        </div>
        
        {isLoadingStats ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
          </div>
        ) : stats ? (
          <>
            {/* Cartes de statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">Campagnes Totales</CardTitle>
                  <Award className="h-4 w-4 text-yellow-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">{stats.totalCampaigns}</div>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.activeCampaigns} actives, {stats.completedCampaigns} terminées
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">Montant Collecté</CardTitle>
                  <DollarSign className="h-4 w-4 text-yellow-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">{formatCurrency(stats.totalCollectedAmount)} FCFA</div>
                  <p className="text-xs text-gray-400 mt-1">
                    {((stats.totalCollectedAmount / stats.totalGoalAmount) * 100).toFixed(2)}% de l'objectif total
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">Contributions</CardTitle>
                  <Users className="h-4 w-4 text-yellow-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">{stats.totalContributions}</div>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.totalContributions > 0 
                      ? `Moyenne: ${formatCurrency(stats.totalCollectedAmount / stats.totalContributions)} FCFA` 
                      : 'Aucune contribution'}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">Taux de Réussite</CardTitle>
                  <TrendingUp className="h-4 w-4 text-yellow-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">{stats.successRate.toFixed(2)}%</div>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.completedCampaigns} campagnes ont atteint leur objectif
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Graphiques */}
            <Tabs defaultValue="campaigns" className="w-full">
              <TabsList className="bg-white/5 border border-white/10 text-gray-100">
                <TabsTrigger className="data-[state=active]:bg-white/10 data-[state=active]:text-yellow-300" value="campaigns">Campagnes</TabsTrigger>
                <TabsTrigger className="data-[state=active]:bg-white/10 data-[state=active]:text-yellow-300" value="amounts">Montants</TabsTrigger>
              </TabsList>
              
              <TabsContent value="campaigns" className="mt-4">
                <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                  <CardHeader>
                    <CardTitle>Répartition des Campagnes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={campaignStatusData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {campaignStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${value} campagnes`, '']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="amounts" className="mt-4">
                <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                  <CardHeader>
                    <CardTitle>Montants Collectés vs Objectifs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            { name: 'Objectif', value: stats.totalGoalAmount },
                            { name: 'Collecté', value: stats.totalCollectedAmount }
                          ]}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                          <XAxis dataKey="name" stroke="#999" />
                          <YAxis stroke="#999" tickFormatter={(value) => `${formatCurrency(value)} FCFA`} />
                          <Tooltip formatter={(value) => [`${formatCurrency(value)} FCFA`, '']} />
                          <Bar dataKey="value" fill="#eab308" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            {/* Contributions récentes */}
            <Card className="bg-white/5 backdrop-blur-sm border-white/10">
              <CardHeader>
                <CardTitle>Contributions Récentes</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingContributions ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                  </div>
                ) : recentContributions && recentContributions.length > 0 ? (
                  <div className="space-y-4">
                    {recentContributions.map((contribution: any) => (
                      <div key={contribution.id} className="flex items-start space-x-4 p-3 bg-black/20 rounded-lg">
                        <div className="bg-yellow-500/20 rounded-full p-2">
                          <Heart className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <div>
                              <p className="font-medium text-white">
                                {contribution.is_anonymous 
                                  ? 'Anonyme' 
                                  : contribution.contributor_name || 'Contributeur'}
                              </p>
                              <p className="text-sm text-gray-400">
                                a contribué à <span className="text-yellow-400">{contribution.campaign?.title}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-yellow-400">{formatCurrency(contribution.amount)} FCFA</p>
                              <p className="text-xs text-gray-400">
                                {new Date(contribution.created_at).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          {contribution.message && (
                            <p className="text-sm text-gray-300 mt-2 italic">"{contribution.message}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Heart className="h-12 w-12 text-yellow-400/30 mx-auto mb-3" />
                    <p className="text-gray-400">Aucune contribution récente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-16 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl">
            <Calendar className="h-16 w-16 text-yellow-400/50 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Aucune donnée disponible</h3>
            <p className="text-gray-400 max-w-md mx-auto">
              Les statistiques seront disponibles une fois que des campagnes et des contributions auront été créées.
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
